/**
 * The live chat socket: one shared connection to websocket-server's `/web`
 * namespace (live web chat, Michael 10/8), held open only while something on
 * the page wants it — a widget with an unresolved chat, or the Chats inbox.
 *
 * websocket-server rides on pom-2-api's aware-api-* host (Carlos 10/9: an
 * ELB listener rule routes /socket.io* on the aware-api-* load balancers to
 * websocket-server's target group, alongside node-api's own protect-* host
 * for the mobile app), the same host the REST client talks to — but the
 * hosts are still listed here rather than derived from src/http/index.ts,
 * since this module cares about the socket host specifically, not the REST
 * base URL. The handshake carries the Safety Hub login token (the same one
 * the REST client sends); the server checks it like pom-2-api's verifyJwt.
 *
 * Everything on `/web` is ephemeral and one-way: the server sends
 * `chat_update` (a doorbell — the client refetches over HTTP) and
 * `chat_typing`; the client sends nothing, and nothing is acked. A missed
 * event is harmless because both consumers keep a slow fallback poll.
 *
 * A server-side rejection (lane off: "Invalid namespace"; token refused:
 * "Authentication failed") stops socket.io's own reconnection — `active` is
 * false on the socket — so this module retries on its own, slowly (30 s,
 * doubling to 5 min), rather than hammering the SOS server or giving up for
 * the life of the page. Transport drops keep socket.io's built-in backoff.
 *
 * The server fixes the socket's identity at the handshake (who owns which
 * chats, whether it is a POM Master). Masquerade start/stop changes that
 * identity without a new token — the server flips it on the token's row and
 * `whoIam` replaces `authUser` — so the socket reconnects whenever the
 * token or the signed-in user (id, masquerade) changes in the store.
 */
import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { store } from '../../../store';
import type { ChatStatus } from './support-agent-api';

export const LIVE_CHAT_SOCKET_HOSTS: Record<string, string> = {
  local: 'https://aware-api-dev.pom-bright.com',
  dev: 'https://aware-api-dev.pom-bright.com',
  stg: 'https://aware-api-qa.pom-bright.com',
  prd: 'https://aware-api.pom-bright.com',
  // Like the mobile app: ngrok doesn't carry the socket, so dev's does.
  ngrok: 'https://aware-api-dev.pom-bright.com',
};
export const LIVE_CHAT_SOCKET_NAMESPACE = '/web';
export const LIVE_CHAT_SOCKET_PATH = '/socket.io';
export const LIVE_CHAT_RETRY_MIN_MS = 30_000;
export const LIVE_CHAT_RETRY_MAX_MS = 5 * 60_000;

/** What the server's handshake decides from: token, user, masquerade. */
export function liveChatIdentityKey(): string {
  const auth = store.getState().auth as { token?: string; authUser?: { id?: string; masquarade?: boolean } };
  const token = auth?.token ?? '';
  if (!token) return '';
  return `${token}|${auth?.authUser?.id ?? ''}|${auth?.authUser?.masquarade ? 1 : 0}`;
}

export function liveChatSocketUrl(env: string | undefined = process.env.REACT_APP_ENV): string {
  const host = LIVE_CHAT_SOCKET_HOSTS[env || 'dev'] ?? LIVE_CHAT_SOCKET_HOSTS.dev;
  return host + LIVE_CHAT_SOCKET_NAMESPACE;
}

// websocket-server's supportAgentChatsWatcher payloads. `chat_update` is the
// same doorbell the mobile app gets (minus eventId); `chat_typing` goes to
// the chat's owner only while a POM Master types in an escalated chat.
export interface ChatUpdateEvent {
  type?: 'CHAT_UPDATE';
  chatId: string;
  status?: ChatStatus | null;
  updatedAt?: string | null;
}
export interface ChatTypingEvent {
  type?: 'CHAT_TYPING';
  chatId: string;
  typing: boolean;
  name?: string | null;
  at?: string;
}
// To every connected POM Master while a chat's owner types in an escalated
// chat (Michael 10/10).
export interface ChatCustomerTypingEvent {
  type?: 'CHAT_CUSTOMER_TYPING';
  chatId: string;
  typing: boolean;
  at?: string;
}
// To every connected POM Master when a chat's owner has the chat in front
// of them (Michael 10/13): pom-2-api's POST /chat/:id/seen stamped
// `lastSeenAt` (from the web widget or the app). The inbox shows "Seen"
// under the last reply once the stamp reaches it.
export interface ChatSeenEvent {
  type?: 'CHAT_SEEN';
  chatId: string;
  lastSeenAt: string;
}
// To every connected POM Master while a rep types in an escalated chat
// (websocket-server #78): `by` is that rep's email, so the inbox can leave
// out its own rep's typing. A stop carries `by: null`.
export interface ChatStaffTypingEvent {
  type?: 'CHAT_STAFF_TYPING';
  chatId: string;
  typing: boolean;
  name?: string | null;
  by?: string | null;
  at?: string;
}
interface LiveChatEvents {
  chat_update: ChatUpdateEvent;
  chat_typing: ChatTypingEvent;
  chat_staff_typing: ChatStaffTypingEvent;
  chat_customer_typing: ChatCustomerTypingEvent;
  chat_seen: ChatSeenEvent;
}
export type LiveChatEventName = keyof LiveChatEvents;
type Listener<E extends LiveChatEventName> = (payload: LiveChatEvents[E]) => void;

export class LiveChatSocket {
  private socket: Socket | null = null;
  private holds = 0;
  private connectedState = false;
  private retryTimer: number | null = null;
  private retryDelayMs = LIVE_CHAT_RETRY_MIN_MS;
  private identityKey = '';
  private unsubscribeStore: (() => void) | null = null;
  private readonly listeners: { [E in LiveChatEventName]: Set<Listener<E>> } = {
    chat_update: new Set(),
    chat_typing: new Set(),
    chat_staff_typing: new Set(),
    chat_customer_typing: new Set(),
    chat_seen: new Set(),
  };
  private readonly connectionListeners = new Set<() => void>();

  get connected(): boolean {
    return this.connectedState;
  }

  /** Keep the connection open until the returned release is called. */
  acquire(): () => void {
    this.holds += 1;
    if (this.holds === 1) this.connect();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holds -= 1;
      if (this.holds === 0) this.disconnect();
    };
  }

  on<E extends LiveChatEventName>(event: E, listener: Listener<E>): () => void {
    const set = this.listeners[event] as Set<Listener<E>>;
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }

  subscribeConnection(listener: () => void): () => void {
    this.connectionListeners.add(listener);
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  private connect() {
    const socket = io(liveChatSocketUrl(), {
      path: LIVE_CHAT_SOCKET_PATH,
      transports: ['websocket'],
      // Read on every (re)connect attempt, so a refreshed login is used.
      auth: (cb) => cb({ token: store.getState().auth.token }),
    });
    socket.on('connect', () => {
      this.retryDelayMs = LIVE_CHAT_RETRY_MIN_MS;
      this.setConnected(true);
    });
    socket.on('disconnect', () => this.setConnected(false));
    socket.on('connect_error', () => {
      this.setConnected(false);
      if (!socket.active) this.scheduleRetry();
    });
    socket.on('chat_update', (payload: ChatUpdateEvent) => this.dispatch('chat_update', payload));
    socket.on('chat_typing', (payload: ChatTypingEvent) => this.dispatch('chat_typing', payload));
    socket.on('chat_staff_typing', (payload: ChatStaffTypingEvent) => this.dispatch('chat_staff_typing', payload));
    socket.on('chat_customer_typing', (payload: ChatCustomerTypingEvent) => this.dispatch('chat_customer_typing', payload));
    socket.on('chat_seen', (payload: ChatSeenEvent) => this.dispatch('chat_seen', payload));
    this.socket = socket;

    this.identityKey = liveChatIdentityKey();
    this.unsubscribeStore = store.subscribe(() => {
      const key = liveChatIdentityKey();
      if (key === this.identityKey || !this.socket) return;
      this.identityKey = key;
      this.clearRetry();
      this.socket.disconnect();
      // Logged out: nothing to handshake with; the holders are on their way out.
      if (key) this.socket.connect();
    });
  }

  private disconnect() {
    this.clearRetry();
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
    }
    this.retryDelayMs = LIVE_CHAT_RETRY_MIN_MS;
    this.setConnected(false);
  }

  private scheduleRetry() {
    if (this.retryTimer !== null || this.holds === 0) return;
    const delay = this.retryDelayMs;
    this.retryDelayMs = Math.min(this.retryDelayMs * 2, LIVE_CHAT_RETRY_MAX_MS);
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      if (this.holds > 0 && this.socket) this.socket.connect();
    }, delay);
  }

  private clearRetry() {
    if (this.retryTimer !== null) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private setConnected(connected: boolean) {
    if (this.connectedState === connected) return;
    this.connectedState = connected;
    this.connectionListeners.forEach((listener) => listener());
  }

  private dispatch<E extends LiveChatEventName>(event: E, payload: LiveChatEvents[E]) {
    if (!payload || typeof payload !== 'object' || typeof payload.chatId !== 'string') return;
    (this.listeners[event] as Set<Listener<E>>).forEach((listener) => listener(payload));
  }
}

export const liveChatSocket = new LiveChatSocket();

/**
 * Holds the shared socket open while `wanted` is true; returns whether it is
 * connected right now (false whenever not wanted), so callers can slow their
 * fallback poll down while live updates are flowing.
 */
export function useLiveChatSocket(wanted: boolean): boolean {
  const [connected, setConnected] = useState(() => wanted && liveChatSocket.connected);
  useEffect(() => {
    if (!wanted) {
      setConnected(false);
      return undefined;
    }
    const release = liveChatSocket.acquire();
    const unsubscribe = liveChatSocket.subscribeConnection(() => setConnected(liveChatSocket.connected));
    setConnected(liveChatSocket.connected);
    return () => {
      unsubscribe();
      release();
    };
  }, [wanted]);
  return wanted && connected;
}

/** Runs `handler` (always the latest one) for each `event` while `wanted`. */
export function useLiveChatEvent<E extends LiveChatEventName>(
  wanted: boolean,
  event: E,
  handler: (payload: LiveChatEvents[E]) => void,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!wanted) return undefined;
    return liveChatSocket.on(event, (payload) => handlerRef.current(payload));
  }, [wanted, event]);
}
